import { ErrorMessage, Field, Form, FormikProvider, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { CustomButton, SmallBackButton } from "../../../../core/components/CustomButton";
import { Column } from "../../../../core/components/Column";
import { CustomInput } from "../../../../core/components/CustomInput";
import { ModalSimple } from "../../../../core/components/Modal";
import { Row } from "../../../../core/components/Row";
import useDebounce from "../../../../core/hooks/useDebounce";
import { CircleArrow } from "../../../../core/icons/CircelArrow";
import {
	getDisplayLocationLabel,
} from "../../../../core/utilities/locationStorage";
import { setResetSearch } from "../../../match/presentation/store/MatchSlice";
import { UpdateUserParams } from "../../../profile/domain/usecases/updateUser";
import { onUpdateUser } from "../../../profile/presentation/store/OnboardingThunk";
import { searchLocationValidationSchema } from "../interface/SearchLocationFormInterface";
import { onGetDetailPlaces, onGetPlaces } from "../store/SettingsThunk";

interface PendingSelection {
	description: string;
	placeId?: string | null;
	lat?: number;
	lng?: number;
	useCurrentLocation?: boolean;
}

export const SearchLocationScreen = () => {
	const navigate = useNavigate();
	const dispatch: AppDispatch = useDispatch();
	const { search, settingsLoading } = useSelector((state: RootState) => state.settings);
	const { user } = useSelector((state: RootState) => state.onboarding);

	const [isButtonLoading, setIsButtonLoading] = useState(false);
	const [isListVisible, setIsListVisible] = useState(false);
	const [searchValue, setSearchValue] = useState<string>("");
	const [selectedPlaceId, setSelectedPlaceId] = useState<string | null>(null);
	const [pendingSelection, setPendingSelection] = useState<PendingSelection | null>(null);
	const [locationError, setLocationError] = useState("");

	const currentLocationLabel = getDisplayLocationLabel({
		city: user?.city,
		country: user?.country,
	});

	const formik = useFormik({
		initialValues: {
			search: [{ description: searchValue, place_id: selectedPlaceId }],
		},
		enableReinitialize: true,
		validationSchema: searchLocationValidationSchema,
		validateOnChange: false,
		validateOnBlur: true,
		onSubmit: async (values) => {
			const selectedLocation = values.search[0]?.description || "";
			const selectedId = values.search[0]?.place_id || "";
			setSearchValue(selectedLocation);
			setSelectedPlaceId(selectedId);
			setPendingSelection({
				description: selectedLocation,
				placeId: selectedId,
			});
		},
	});

	const debounceSearchValue = useDebounce(searchValue, 1000);

	useEffect(() => {
		if (debounceSearchValue.trim()) {
			dispatch(onGetPlaces({ search: debounceSearchValue }));
		}
	}, [debounceSearchValue, dispatch]);

	const handleSelect = (description: string, placeId: string) => {
		formik.setFieldValue("search", [{ place_id: placeId, description }]);
		setSearchValue(description);
		setSelectedPlaceId(placeId);
		setIsListVisible(false);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchValue(value);
		formik.setFieldValue("search[0].description", value);
		setIsListVisible(value.trim() !== "");
	};

	const handleLocationSaved = () => {
		dispatch(setResetSearch());
		navigate("/settings");
	};

	const handleUseCurrentLocation = () => {
		setLocationError("");
		navigator.geolocation.getCurrentPosition(
			(position) => {
				const lat = position.coords.latitude;
				const lng = position.coords.longitude;
				setPendingSelection({
					description: currentLocationLabel || "Current location",
					lat,
					lng,
					useCurrentLocation: true,
				});
			},
			() => {
				setLocationError("We couldn't read your device location. Choose a town or city below instead.");
			}
		);
	};

	const handleConfirmSelection = async () => {
		if (!pendingSelection) return;
		setIsButtonLoading(true);
		const selectedLocation = pendingSelection.description.trim();

		if (pendingSelection.useCurrentLocation && pendingSelection.lat !== undefined && pendingSelection.lng !== undefined) {
			const lat = pendingSelection.lat.toString();
			const lng = pendingSelection.lng.toString();
			const result = await dispatch(
				onUpdateUser(
					new UpdateUserParams({
						lat,
						lng,
						profile_location_source: "browser_location",
						discovery_override_lat: "",
						discovery_override_lng: "",
						discovery_override_label: "",
					})
				)
			);
			if (onUpdateUser.fulfilled.match(result)) {
				handleLocationSaved();
			}
		} else if (pendingSelection.placeId) {
			const result = await dispatch(onGetDetailPlaces({ place_id: pendingSelection.placeId }));
			const details = result.payload as {
				location?: { lat?: number; lng?: number };
				formattedAddress?: string;
				formatted_address?: string;
			} | undefined;
			const placeLat = details?.location?.lat;
			const placeLng = details?.location?.lng;
			const placeDescription =
				selectedLocation ||
				details?.formattedAddress ||
				details?.formatted_address ||
				"";
			if (placeLat !== undefined && placeLng !== undefined) {
				const action = await dispatch(
					onUpdateUser(
						new UpdateUserParams({
							discovery_override_lat: placeLat.toString(),
							discovery_override_lng: placeLng.toString(),
							discovery_override_label: placeDescription,
						})
					)
				);

				if (onUpdateUser.fulfilled.match(action)) {
					handleLocationSaved();
				}
			}
		}

		setIsButtonLoading(false);
		setPendingSelection(null);
	};

	return (
		<>
			<FormikProvider value={formik}>
				<Column className="bg-white p-6 md:p-8 gap-4 md:gap-5 max-md:overflow-y-auto">
					<SmallBackButton onClick={() => navigate("/settings")} className="mb-2" />
					<h1 className="text-2xl font-semibold">Change search location</h1>
					<p className="text-gray-500">
						Type a town or city below and select a result. You can search without allowing device location access.
					</p>
					<div className="rounded-lg border border-gray-300 bg-white p-4">
						<button
							type="button"
							className="w-full text-left font-semibold text-primary hover:underline"
							onClick={handleUseCurrentLocation}
						>
							Use current location
						</button>
						{locationError && (
							<div className="mt-2 text-sm text-red-500">{locationError}</div>
						)}
					</div>
					<Form className="flex flex-col gap-4">
						<div className="relative">
							<Field
								as={CustomInput}
								name="search[0].description"
								hintText="Search"
								label="Town or city"
								type="text"
								className="form-control"
								autoComplete={searchValue ? "off" : undefined}
								onChange={handleInputChange}
								onBlur={formik.handleBlur}
								value={formik.values.search[0]?.description || searchValue}
								required
								isLoading={settingsLoading && debounceSearchValue === searchValue}
								disabled={settingsLoading}
							/>
							<ErrorMessage name="search[0].description" component="div" className="text-red-500" />
							{search.length > 0 && isListVisible && searchValue.trim() !== "" && (
								<div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
									<ul className="list-none py-1">
										{search.map((place, index) => (
											<li
												key={index}
												className="flex items-center px-2 hover:bg-gray-100 cursor-pointer"
												onClick={() => handleSelect(place.description ?? "", place.place_id ?? "")}
											>
												<Row className="my-3" crossAxisAlignment="center">
													<CircleArrow className="w-9 h-9 mr-2" />
													{place.description}
												</Row>
											</li>
										))}
									</ul>
								</div>
							)}
							{search.length === 0 && searchValue.trim() !== "" && (
								<div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
									<div className="text-center py-2">No locations found.</div>
								</div>
							)}
						</div>
						<CustomButton
							type="submit"
							className="mt-4"
							loading={isButtonLoading}
							disabled={isButtonLoading || !formik.values.search[0]?.place_id}
						>
							Continue
						</CustomButton>
					</Form>
					<p className="text-sm text-gray-500">
						Choosing a town changes where you search. It does not erase a previously saved profile location or change your browser's location permission. You can block device location access in your browser's site settings.
					</p>
				</Column>
			</FormikProvider>
			<ModalSimple
				title="Change location"
				variant="secondary"
				actionExecuted={pendingSelection !== null}
				onClose={() => setPendingSelection(null)}
				onAction={handleConfirmSelection}
				next
			>
				Are you sure you want to change your location to{" "}
				<span className="text-primary font-semibold">
					{pendingSelection?.description || currentLocationLabel}
				</span>
				?
			</ModalSimple>
		</>
	);
};
